import { hasPermission } from 'core/auth/rbac'
import { Badge } from 'core/components/ui/Badge'
import { Modal } from 'core/components/ui/Modal'
import { formatDateTime } from 'core/utils/format'
import { useAuth } from 'features/auth/presentation/context/AuthContext'
import React, { useEffect, useState } from 'react'
import { formatOrderAddress, OrderListItem, OrderStatus, StatusHistoryEntry } from '../../domain/entities/Order'
import { useOrderDetail } from '../hooks/useOrderDetail'
import { OrderStatusStepper } from './OrderStatusStepper'

interface OrderDetailModalProps {
  isOpen: boolean
  onClose: () => void
  order: OrderListItem | null
  onUpdateStatus?: (status: OrderStatus) => Promise<void>
}

const formatCurrency = (val: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(val)
}

export const OrderDetailModal: React.FC<OrderDetailModalProps> = ({ isOpen, onClose, order, onUpdateStatus }) => {
  const { user } = useAuth()
  const { detail, loading: detailLoading, error: detailError } = useOrderDetail(isOpen ? (order?.id ?? null) : null)
  const [actionError, setActionError] = useState<string | null>(null)

  useEffect(() => {
    setActionError(null)
  }, [order?.id, isOpen])

  if (!order) return null

  // Check roles permitted to update order status (AM, Supervisor)
  const canUpdateStatus = hasPermission(user, 'update_order_status')

  const handleStatusChange = async (nextStatus: OrderStatus) => {
    if (!onUpdateStatus) return

    setActionError(null)
    try {
      await onUpdateStatus(nextStatus)
    } catch (err: unknown) {
      const errorVal = err as Error
      setActionError(errorVal.message || 'Failed to update order status.')
    }
  }

  const items = detail?.items || []
  const totalUnits = items.reduce((sum, item) => sum + item.units, 0)

  const footerActions = (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 w-full">
      {/* Advancing Status Options (Contextual Actions) */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
        {canUpdateStatus && order.status === 'order_placed' && (
          <button
            onClick={() => handleStatusChange('payment_received')}
            className="px-3 py-1.5 text-xs font-semibold bg-primary hover:bg-primary-hover text-primary-foreground rounded-btn transition-colors w-full sm:w-auto"
          >
            Mark Payment Received
          </button>
        )}
        {canUpdateStatus && order.status === 'payment_received' && (
          <button
            onClick={() => handleStatusChange('order_confirmed')}
            className="px-3 py-1.5 text-xs font-semibold bg-primary hover:bg-primary-hover text-primary-foreground rounded-btn transition-colors w-full sm:w-auto"
          >
            Confirm Order
          </button>
        )}
        {canUpdateStatus && order.status === 'order_confirmed' && (
          <button
            onClick={() => handleStatusChange('in_progress')}
            className="px-3 py-1.5 text-xs font-semibold bg-primary hover:bg-primary-hover text-primary-foreground rounded-btn transition-colors w-full sm:w-auto"
          >
            Mark In Progress
          </button>
        )}
        {canUpdateStatus && order.status === 'in_progress' && (
          <button
            onClick={() => handleStatusChange('dispatched')}
            className="px-3 py-1.5 text-xs font-semibold bg-primary hover:bg-primary-hover text-primary-foreground rounded-btn transition-colors w-full sm:w-auto"
          >
            Mark Dispatched
          </button>
        )}
        {canUpdateStatus && order.status === 'dispatched' && (
          <button
            onClick={() => handleStatusChange('delivered')}
            className="px-3 py-1.5 text-xs font-semibold bg-primary hover:bg-primary-hover text-primary-foreground rounded-btn transition-colors w-full sm:w-auto"
          >
            Mark Delivered
          </button>
        )}
      </div>

      <button
        onClick={onClose}
        className="px-4 py-2 text-sm font-semibold border border-border rounded-btn bg-surface hover:bg-bg text-text-secondary hover:text-text-primary transition-colors w-full sm:w-auto sm:ml-auto"
      >
        Close Detail
      </button>
    </div>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Order Indent Summary - ${order.id}`}
      footer={footerActions}
      size="xl"
    >
      <div className="space-y-6">
        {actionError && (
          <div className="p-3 text-xs bg-danger/10 border border-danger/20 text-danger rounded-btn font-medium">
            {actionError}
          </div>
        )}

        {/* Horizontal status stepper */}
        <div className="bg-bg/40 border border-border rounded-card">
          <OrderStatusStepper currentStatus={order.status} hasGrievance={order.hasGrievance} />
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Column 1: Generic Buyer Details */}
          <div className="border border-border p-4 rounded-card space-y-3 bg-surface text-left">
            <span className="text-xs font-bold text-text-secondary uppercase tracking-wider block border-b border-border pb-1">
              Buyer Details
            </span>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between items-start gap-4">
                <span className="text-text-secondary">Name:</span>
                <span className="font-semibold text-text-primary text-right">{order.buyer?.name || '—'}</span>
              </div>
              {order.buyer?.businessName && (
                <div className="flex justify-between items-start gap-4">
                  <span className="text-text-secondary">Business Name:</span>
                  <span className="font-semibold text-text-primary text-right">{order.buyer.businessName}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-text-secondary">Contact Number:</span>
                <span className="font-semibold text-text-primary">{order.buyer?.phone || '—'}</span>
              </div>
              {order.buyer?.email && (
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary">Email Address:</span>
                  <span className="font-semibold text-text-primary">{order.buyer.email}</span>
                </div>
              )}
              <div className="flex justify-between items-start gap-4">
                <span className="text-text-secondary">Address:</span>
                <span className="font-semibold text-text-primary text-right">
                  {formatOrderAddress(order.shipToAddress)}
                </span>
              </div>
              {order.buyer?.role && (
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary">Buyer Type:</span>
                  <Badge tone="neutral" variant="outline" className="capitalize font-bold">
                    {order.buyer.role}
                  </Badge>
                </div>
              )}
            </div>
          </div>

          {/* Column 2: Order Calculation Summary */}
          <div className="border border-border p-4 rounded-card space-y-3 bg-surface text-left">
            <span className="text-xs font-bold text-text-secondary uppercase tracking-wider block border-b border-border pb-1">
              Calculation Summary
            </span>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between items-center">
                <span className="text-text-secondary">Created At:</span>
                <span className="font-semibold text-text-primary">
                  {formatDateTime(order.orderPlacedDate || order.createdAt)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-text-secondary">Quantity Ordered:</span>
                <span className="font-bold text-text-primary">{totalUnits} Units</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-text-secondary">Payment Type:</span>
                <span className="font-semibold text-text-primary uppercase">{order.paymentMode}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-text-secondary">Payment Status:</span>
                <Badge
                  tone={order.paymentStatus === 'verified' ? 'success' : 'warning'}
                  variant="subtle"
                  className="capitalize"
                >
                  {order.paymentStatus}
                </Badge>
              </div>
              {order.hasGrievance && (
                <div className="flex justify-between items-center">
                  <span className="text-text-secondary">Grievance:</span>
                  <Badge tone="danger" variant="subtle" className="font-bold">
                    Raised
                  </Badge>
                </div>
              )}
              <div className="flex justify-between items-center font-bold text-text-primary border-t border-border pt-2 text-sm">
                <span>Total Amount:</span>
                <span>{formatCurrency(order.totalAmount || 0)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Line Items Table */}
        <div className="space-y-3">
          <h4 className="text-xs font-bold text-text-primary uppercase tracking-wider block border-b border-border pb-1 text-left">
            Order Line Items ({items.length})
          </h4>
          {detailError && (
            <div className="p-3 text-xs bg-danger/10 border border-danger/20 text-danger rounded-btn font-medium">
              {detailError}
            </div>
          )}
          {detailLoading && !detail && (
            <div className="p-4 text-xs text-text-secondary text-center border border-border rounded-card">
              Loading line items...
            </div>
          )}
          {!detailLoading && items.length > 0 && (
            <div className="border border-border rounded-card overflow-x-auto">
              <table className="min-w-full divide-y divide-border table-auto text-left text-xs">
                <thead className="bg-bg text-text-secondary font-semibold uppercase">
                  <tr>
                    <th className="px-4 py-2">Product</th>
                    <th className="px-4 py-2">Variant</th>
                    <th className="px-4 py-2 text-right">Units Qty</th>
                    <th className="px-4 py-2 text-right">Price / Unit</th>
                    <th className="px-4 py-2 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-surface text-text-primary">
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td className="px-4 py-3 font-medium">{item.productName}</td>
                      <td className="px-4 py-3 font-mono text-text-secondary">{item.variant}</td>
                      <td className="px-4 py-3 text-right">{item.units} Units</td>
                      <td className="px-4 py-3 text-right">
                        {item.unitPrice !== undefined ? formatCurrency(item.unitPrice) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right font-bold">
                        {item.subtotal !== undefined ? formatCurrency(item.subtotal) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Status History Timeline */}
        <StatusHistoryTimeline history={detail?.statusHistory ?? []} loading={detailLoading && !detail} />
      </div>
    </Modal>
  )
}

const StatusHistoryTimeline: React.FC<{ history: StatusHistoryEntry[]; loading: boolean }> = ({ history, loading }) => (
  <div className="space-y-3">
    <h4 className="text-xs font-bold text-text-primary uppercase tracking-wider block border-b border-border pb-1 text-left">
      Status History
    </h4>
    {loading && (
      <div className="p-4 text-xs text-text-secondary text-center border border-border rounded-card">
        Loading history...
      </div>
    )}
    {!loading && history.length === 0 && (
      <div className="p-3 text-xs text-text-secondary text-center border border-border rounded-card">
        No status history available.
      </div>
    )}
    {!loading && history.length > 0 && (
      <div className="border border-border rounded-card bg-surface overflow-hidden">
        {history.map((entry, idx) => (
          <div
            key={`${entry.status}-${entry.changedAt}`}
            className={`flex items-center gap-3 px-4 py-3 text-xs ${idx < history.length - 1 ? 'border-b border-border' : ''}`}
          >
            <div className="h-2 w-2 rounded-full bg-status-active flex-shrink-0" />
            <span className="font-semibold text-text-primary capitalize flex-1">{entry.status.replace(/_/g, ' ')}</span>
            <span className="text-text-secondary whitespace-nowrap">{formatDateTime(entry.changedAt)}</span>
          </div>
        ))}
      </div>
    )}
  </div>
)
